import './MultiDocumentList.scss';
import { BsArrowReturnRight } from 'react-icons/bs';
import { MdAdd, MdDeleteOutline, MdEdit, MdOpenInNew } from 'react-icons/md';
import { OnboardingMultiDocument } from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { MenuItem } from '@Components/Dropdown';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { BPMTable, Header } from '@Components/StickyTable';
import { dateToLongDate } from '@src/utils/date-utils';
import {
  MultiDocumentBody,
  MultiDocumentLayout,
  MultiDocumentTableCopy
} from '../multi-document-constants';
import { latestUpdate } from '../multi-document-utils';
import { KebabMenu } from './KebabMenu';
import { SectionCardBody } from './SectionCardBody';
import { SectionFeedsFooter } from './SectionFeedsFooter';

const DocumentAction = {
  OPEN: 'open',
  EDIT: 'edit',
  REMOVE: 'remove'
} as const;
type DocumentActionType = (typeof DocumentAction)[keyof typeof DocumentAction];

interface MultiDocumentListProps {
  documents: OnboardingMultiDocument[];
  isLoading: boolean;
  isError: boolean;
  body: MultiDocumentBody;
  addButtonLabel: string;
  footerCopy: string;
  onAdd: () => void;
  onOpen: (doc: OnboardingMultiDocument) => void;
  onEdit: (doc: OnboardingMultiDocument) => void;
  onRemove: (doc: OnboardingMultiDocument) => void;
}

export const MultiDocumentList = ({
  documents,
  isLoading,
  isError,
  body,
  addButtonLabel,
  footerCopy,
  onAdd,
  onOpen,
  onEdit,
  onRemove
}: MultiDocumentListProps) => {
  const latest = latestUpdate(documents);
  const onAction = (doc: OnboardingMultiDocument) => (value: DocumentActionType) => {
    if (value === DocumentAction.OPEN) {
      onOpen(doc);
    } else if (value === DocumentAction.EDIT) {
      onEdit(doc);
    } else {
      onRemove(doc);
    }
  };
  return (
    <div className='multi-document-list-section'>
      {body.layout === MultiDocumentLayout.TABLE && (
        <p className='multi-document-intro'>{body.intro}</p>
      )}
      <SectionCardBody
        isLoading={isLoading}
        isError={isError}
        isEmpty={documents.length === 0}
        errorCopy="Couldn't load this section."
        emptyCopy={body.layout === MultiDocumentLayout.LIST ? body.emptyStateCopy : undefined}
        emptyAction={
          <Button size={Size.LG} leadingElement={<MdAdd />} onClick={onAdd}>
            {addButtonLabel}
          </Button>
        }
      >
        {body.layout === MultiDocumentLayout.TABLE ? (
          <DocumentTable documents={documents} copy={body.table} onAction={onAction} />
        ) : (
          <div className='multi-document-scroll'>
            {documents.map(doc => (
              <DocumentRow key={doc.id} doc={doc} onAction={onAction(doc)} />
            ))}
          </div>
        )}
      </SectionCardBody>
      <SectionFeedsFooter
        className='multi-document-footer'
        icon={<BsArrowReturnRight />}
        feedsCopy={footerCopy}
        updatedAt={latest?.updatedAt}
        updatedByName={latest?.updatedByName}
      />
    </div>
  );
};

const DOCUMENT_ACTION_ITEMS: MenuItem<DocumentActionType>[] = [
  { value: DocumentAction.OPEN, label: 'Open', leadingContent: <MdOpenInNew /> },
  { value: DocumentAction.EDIT, label: 'Edit', leadingContent: <MdEdit /> },
  { value: DocumentAction.REMOVE, label: 'Remove', leadingContent: <MdDeleteOutline /> }
];

interface DocumentRowProps {
  doc: OnboardingMultiDocument;
  onAction: (value: DocumentActionType) => void;
}

const DocumentRow = ({ doc, onAction }: DocumentRowProps) => (
  <div className='multi-document-row'>
    <div className='multi-document-row-main'>
      <DocumentTitle doc={doc} onAction={onAction} />
      {doc.note && <div className='multi-document-row-note'>{doc.note}</div>}
      <div className='multi-document-row-added'>
        added {dateToLongDate(doc.addedAt)} by {doc.addedByName}
      </div>
    </div>
    <KebabMenu<DocumentActionType> items={DOCUMENT_ACTION_ITEMS} onSelect={onAction} />
  </div>
);

interface DocumentTableProps {
  documents: OnboardingMultiDocument[];
  copy: MultiDocumentTableCopy;
  onAction: (doc: OnboardingMultiDocument) => (value: DocumentActionType) => void;
}

const DOCUMENT_TABLE_ROW_HEIGHT = 64;

const documentTableHeaders = (
  copy: MultiDocumentTableCopy,
  onAction: DocumentTableProps['onAction']
): Header[] => [
  {
    name: 'title',
    label: copy.itemColumnLabel,
    flex: 36,
    nonInteractive: true,
    renderer: (doc: OnboardingMultiDocument) => (
      <div className='multi-document-row-main'>
        <DocumentTitle doc={doc} onAction={onAction(doc)} />
      </div>
    )
  },
  {
    name: 'note',
    label: copy.noteColumnLabel,
    flex: 60,
    nonInteractive: true,
    renderer: (doc: OnboardingMultiDocument) => (
      <div className='multi-document-table-note'>{doc.note}</div>
    )
  },
  {
    name: 'actions',
    label: '',
    width: 48,
    nonInteractive: true,
    renderer: (doc: OnboardingMultiDocument) => (
      <KebabMenu<DocumentActionType> items={DOCUMENT_ACTION_ITEMS} onSelect={onAction(doc)} />
    )
  }
];

const DocumentTable = ({ documents, copy, onAction }: DocumentTableProps) => (
  <section className='multi-document-table-section'>
    <h3 className='multi-document-table-heading'>{copy.heading}</h3>
    <BPMTable<OnboardingMultiDocument>
      className='multi-document-table'
      cellClassName='multi-document-table-cell'
      data={documents}
      headers={documentTableHeaders(copy, onAction)}
      filterBar={false}
      autoHeight
      headerHeight={32}
      rowHeight={DOCUMENT_TABLE_ROW_HEIGHT}
    />
  </section>
);

const DocumentTitle = ({ doc, onAction }: DocumentRowProps) => (
  <>
    <InteractiveSpan
      className='multi-document-row-title'
      onClick={() => onAction(DocumentAction.OPEN)}
    >
      {doc.title}
    </InteractiveSpan>
    <div className='multi-document-row-meta'>PDF</div>
  </>
);
